import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Controller } from 'react-hook-form';
import { useTheme } from "@react-navigation/native";
import CustomLabel from '../CustomLabel/CustomLabel';

const CustomControlledTimePicker = ({
    control,
    name,
    rules,
    label,
    error: externalError,
    onTimeChange,
    fontSize = 17,
}) => {
    const [show, setShow] = useState(false);
    const { colors } = useTheme();
    const styles = makeStyles(colors);

    const formatTime = (date) => {
        if (!date) return '';

        const h = date.getHours().toString().padStart(2, '0');
        const m = date.getMinutes().toString().padStart(2, '0');

        return `${h}:${m}`;
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({
                field: { value, onChange },
                fieldState: { error: fieldError },
            }) => {
                const errorMessage =
                    fieldError?.message || externalError;

                const handleChange = (date) => {
                    onChange(date);

                    if (onTimeChange) {
                        onTimeChange(date);
                    }
                };

                if (Platform.OS === 'web') {
                    return (
                        <View style={styles.wrap}>
                            <Text style={styles.label}>{label}</Text>

                            <input
                                type="time"
                                value={formatTime(value)}
                                onChange={(e) => {
                                    const [h, m] = e.target.value
                                        .split(':')
                                        .map(Number);

                                    const date = new Date();
                                    date.setHours(h, m, 0, 0);

                                    handleChange(date);
                                }}
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: errorMessage
                                        ? '1.5px solid #D85A30'
                                        : '1.5px solid #86BC25',
                                    outline: 'none',
                                    fontSize,
                                    padding: 5,
                                    color: colors.text_primary,
                                    fontFamily: 'Roboto-Medium',
                                }}
                            />

                            {errorMessage && (
                                <Text style={styles.error}>
                                    {errorMessage}
                                </Text>
                            )}
                        </View>
                    );
                }

                return (
                    <View style={styles.wrap}>
                        <Text style={styles.label}>{label}</Text>

                        <TouchableOpacity onPress={() => setShow(true)}>
                            <Text
                                style={[
                                    styles.timeText,
                                    !value && styles.placeholder,
                                ]}
                            >
                                {value ? formatTime(value) : '--:--'}
                            </Text>

                            <View
                                style={[
                                    styles.underline,
                                    errorMessage && styles.underlineErr,
                                ]}
                            />
                        </TouchableOpacity>

                        {errorMessage && (
                            <Text style={styles.error}>
                                {errorMessage}
                            </Text>
                        )}

                        {show && (
                            <DateTimePicker
                                value={value || new Date()}
                                mode="time"
                                is24Hour
                                display="spinner"
                                onChange={(event, date) => {
                                    setShow(Platform.OS === 'ios');

                                    if (date) {
                                        handleChange(date);
                                    }
                                }}
                            />
                        )}
                    </View>
                );
            }}
        />
    );
};

const makeStyles = (colors) =>
    StyleSheet.create({
        wrap: {
            flex: 1,
            paddingVertical: 8,
            paddingHorizontal: 4,
            marginHorizontal: 15,
        },
        label: {
            fontSize: 11,
            fontWeight: '600',
            color: colors.green,
            marginBottom: 5,
            textTransform: 'uppercase',
            letterSpacing: 0.5
        },
        timeText: {
            fontSize: 13,
            color: colors.text_primary,
            padding: 5
        },
        placeholder: {
            color: colors.text_placeholder
        },
        underline: {
            height: 1.5,
            backgroundColor: colors.green
        },
        underlineErr: {
            backgroundColor:
                colors.text_rejected
        },
        error: {
            fontSize: 11,
            color: colors.text_rejected,
            marginTop: 3
        },
    });

export default CustomControlledTimePicker;